import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './SplashScreen.css';
import Footer from '../Footer/Footer';
import ScrollTextRotator from './ScrollTextRotator';
import Seo from '../SEO/Seo';
import api from '../../services/api';
import { communityTestimonialService } from '../../services/communityTestimonialService';
import pharmaLogo from '../../assets/logopharmagreen.png';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function safeDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const HERO_PILLARS = [
  { value: '24/7', label: 'Access', detail: 'Programme · module · topic' },
  { value: 'Topic-led', label: 'Structure', detail: 'CAT · EOS · licensing' },
  { value: 'Exam-ready', label: 'Practice', detail: 'Notes · papers · revision' },
];

/** Default testimonials when no community stories are approved yet. */
const SPLASH_TESTIMONIALS = [
  {
    photo:
      'https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&w=320&h=320&q=80&crop=faces',
    photoAlt: 'African medical student portrait',
    quote:
      'Notes and CAT practice are organised by topic — I finally stopped jumping between random PDFs. The self-check questions match how our school sets CATs.',
    name: 'Amina K.',
    roleLine: 'Medical student · Year 4 · Tanzania',
    rating: 5,
  },
  {
    photo:
      'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=320&h=320&q=80&crop=faces',
    photoAlt: 'African clinician portrait',
    quote:
      'Past papers and revision packs saved me before end-of-semester exams. Clear structure and I can track what I have already covered.',
    name: 'Dr. J. Mushi',
    roleLine: 'House officer · Licensing prep',
    rating: 5,
  },
  {
    photo:
      'https://images.unsplash.com/photo-1539577198140-84e9c8b4bce4?auto=format&fit=crop&w=320&h=320&q=80&crop=faces',
    photoAlt: 'African clinical officer student portrait',
    quote:
      'As someone juggling clinic and study, having licensing-oriented practice in one place is worth it. Interface is clean on mobile too.',
    name: 'Peter R.',
    roleLine: 'Clinical officer student · Evening programme',
    rating: 5,
  },
];

function StarRow({ value, id, centered = false }) {
  const full = Math.round(Number(value) || 0);
  return (
    <div
      className={`splash-star-row${centered ? ' splash-star-row--center' : ''}`}
      role="img"
      aria-labelledby={id}
    >
      <span id={id} className="sr-only">
        {full} out of 5 stars
      </span>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          className={`splash-star ${i <= full ? 'splash-star--on' : 'splash-star--off'}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M10 1.5l2.47 5.01 5.53.8-4 3.9.94 5.5L10 14.9 5.06 16.71l.94-5.5-4-3.9 5.53-.8L10 1.5z" />
        </svg>
      ))}
    </div>
  );
}

const SplashScreen = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasFreshNews, setHasFreshNews] = useState(false);
  const [communityTestimonials, setCommunityTestimonials] = useState([]);

  useEffect(() => {
    let mounted = true;

    const loadLatestAnnouncements = async () => {
      try {
        const res = await api.get('/announcements?limit=20');
        const rows = Array.isArray(res.data?.data) ? res.data.data : [];
        const hasRecent = rows.some((item) => {
          const publishedAt = safeDate(item?.published_at) || safeDate(item?.created_at);
          return publishedAt ? Date.now() - publishedAt.getTime() <= ONE_DAY_MS : false;
        });

        if (mounted) setHasFreshNews(hasRecent);
      } catch (_e) {
        if (mounted) setHasFreshNews(false);
      }
    };

    const loadCommunityTestimonials = async () => {
      try {
        const rows = await communityTestimonialService.getPublic(6);
        if (mounted) setCommunityTestimonials(Array.isArray(rows) ? rows : []);
      } catch (_e) {
        if (mounted) setCommunityTestimonials([]);
      }
    };

    loadLatestAnnouncements();
    loadCommunityTestimonials();

    return () => {
      mounted = false;
    };
  }, []);

  const displayedTestimonials = communityTestimonials.length > 0
    ? communityTestimonials
    : SPLASH_TESTIMONIALS.map((item) => ({ ...item, is_community: false }));
  const showingCommunityStories = communityTestimonials.length > 0;

  return (
    <div className="splash-screen">
      <Seo
        title="Pharmacy & Medical Exam Prep Tanzania"
        description="ALIEN GREEN helps pharmacy and medical students in Tanzania prepare for End of Semester, Licensing, and CAT 1/CAT 2 exams with notes, practice exams, and short courses."
        path="/"
        keywords={[
          'ALIEN GREEN',
          'pharmacy exam preparation Tanzania',
          'licensing exam Tanzania',
          'end of semester exam preparation',
          'CAT 1 exam preparation',
          'CAT 2 exam preparation',
          'medical study notes Tanzania',
          'pharmacy short courses',
          'self evaluation tests',
        ]}
      />
      <div className="splash-container">
        {/* Navigation Bar */}
        <nav className="splash-nav">
          <div className="splash-nav-container">
            <Link to="/" className="splash-nav-brand">
              <img
                src={pharmaLogo}
                alt="ALIEN GREEN (EduTechnologies) Logo"
                className="splash-nav-brand-logo"
              />
              <span>
                ALIEN GREEN <span className="brand-parenthetical">(EduTechnologies)</span>
              </span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex splash-nav-links">
              <Link to="/" className="splash-nav-link">Home</Link>
              <Link to="/about" className="splash-nav-link">About</Link>
              <Link to="/services" className="splash-nav-link">Services</Link>
              <Link to="/programmes" className="splash-nav-link">Programmes</Link>
              <Link to="/events" className="splash-nav-link splash-nav-link-with-badge">
                <span>Events &amp; Updates</span>
                {hasFreshNews && <span className="splash-nav-new-badge">NEW</span>}
              </Link>
              <Link to="/login" className="splash-nav-link">Login</Link>
              <Link to="/register" className="splash-nav-link splash-nav-link-primary">Sign Up</Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 bg-gray-700 transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-gray-700 transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-gray-700 transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white shadow-lg">
              <div className="flex flex-col px-4 py-4 space-y-3">
                <Link 
                  to="/" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link 
                  to="/about" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link 
                  to="/services" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link 
                  to="/programmes" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Programmes
                </Link>
                <Link
                  to="/events"
                  className="splash-nav-link splash-nav-link-with-badge px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Events &amp; Updates</span>
                  {hasFreshNews && <span className="splash-nav-new-badge">NEW</span>}
                </Link>
                <Link 
                  to="/login" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="splash-nav-link-primary px-3 py-2 text-center rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Background orbs removed so the rotating background images are clearly visible */}

        {/* Main Content */}
        <div className="splash-main">
          <div className="splash-content">
            {/* Logo/Icon */}
            <div className="splash-logo splash-animate-float">
              <img 
                src={pharmaLogo} 
                alt="Pharma Green Logo" 
                className="logo-svg"
              />
            </div>

            <h1 className="splash-eyebrow splash-eyebrow-main splash-animate-in splash-delay-1">
              <span className="splash-eyebrow-line-1">Your Medical Education</span>
              <span className="splash-eyebrow-line-2">Platform</span>
            </h1>
            <p className="splash-subheadline splash-animate-in splash-delay-2">
              High-quality medical learning materials in one clean platform: structured notes,
              CAT and end-of-semester practice, past papers, and licensing revision support.
            </p>

            <div className="splash-animate-in splash-delay-2">
              <ScrollTextRotator stepPx={240} />
            </div>

            {/* Navigation Buttons */}
            <div className="splash-buttons splash-animate-in splash-delay-4">
              <Link to="/login" className="splash-button splash-button-primary">
                <span>Get Started</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <Link to="/programmes" className="splash-button splash-button-secondary">
                <span>Explore Programmes</span>
              </Link>
              <Link to="/short-courses" className="splash-button splash-button-tertiary">
                <span>Short Courses</span>
              </Link>
            </div>

            <div className="splash-hero-pillars splash-animate-in splash-delay-4" role="list" aria-label="Platform overview">
              {HERO_PILLARS.map((item) => (
                <div key={item.label} className="splash-hero-pillar" role="listitem">
                  <div className="splash-hero-pillar-value">{item.value}</div>
                  <div className="splash-hero-pillar-label">{item.label}</div>
                  <div className="splash-hero-pillar-detail">{item.detail}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Trust: testimonials (above footer) */}
        <div className="splash-trust">
          <section className="splash-trust-inner" aria-labelledby="splash-testimonials-heading">
            <header className="splash-trust-header">
              <p className="splash-trust-eyebrow">Voices from the field</p>
              <h2 id="splash-testimonials-heading" className="splash-trust-title">
                What medical students &amp; clinicians say
              </h2>
              <p className="splash-trust-sub">
                {showingCommunityStories
                  ? 'Real reflections from students and instructors in our community. Each story is admin-approved and shown on the home page for 7 days.'
                  : 'ALIEN GREEN supports medical students and health personnel preparing for end-of-semester exams, licensing tests, and CATs—with structured notes, practice, and self-evaluation in one place. Sample reflections below; share yours from your dashboard after you sign in.'}
              </p>
            </header>

            <div className="splash-testimonial-grid">
              {displayedTestimonials.map((t, idx) => (
                <blockquote key={`${t.name}-${t.id || idx}`} className="splash-testimonial-card">
                  <div className="splash-testimonial-accent" aria-hidden="true" />
                  <div className="splash-testimonial-photo-wrap">
                    <img
                      className="splash-testimonial-photo"
                      src={t.photo}
                      alt={t.photoAlt || ''}
                      width={112}
                      height={112}
                      loading="lazy"
                      decoding="async"
                    />
                    <span className="splash-testimonial-quote-icon" aria-hidden="true">
                      &ldquo;
                    </span>
                  </div>
                  <p className="splash-testimonial-quote">&ldquo;{t.quote}&rdquo;</p>
                  <StarRow value={t.rating} id={`splash-stars-${idx}`} centered />
                  <footer className="splash-testimonial-footer">
                    <cite className="splash-testimonial-name">{t.name}</cite>
                    <div className="splash-testimonial-roleline">{t.roleLine}</div>
                  </footer>
                </blockquote>
              ))}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="splash-footer-wrapper">
          <Footer isSplash={true} />
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
